import api from "@/configs/axios-config";

export type FirstTierSupplier = {
  id: number;
  supplier_code: string;
  supplier_abbr: string;
  supplier_name: string;
  supplier_name_th: string;
  buyer_code_list: Array<string>;
  is_active: boolean;
  active_part: number;
  template_type: "Part" | "Exchange Rate" | "None";
  is_admin_pct: boolean;
  is_profit_pct: boolean;
  admin_oem_formula: string | null;
  profit_oem_formula: string | null;
  admin_spare_formula: string | null;
  profit_spare_formula: string | null;
  update_by: string;
  update_date: string;
};

export type EditFirstTierSupplierPayload = {
  template_type: "Part" | "Exchange Rate" | "None";
  is_admin_pct: boolean;
  is_profit_pct: boolean;
  admin_oem_formula: string | null;
  profit_oem_formula: string | null;
  admin_spare_formula: string | null;
  profit_spare_formula: string | null;
  flagged_reason?: Set<string>;
};

export type getfirstTierParams = {
  skip: number;
  limit: number;
  buyer_code?: string;
};

export const getFirstTierSuppliers = async (
  params?: getfirstTierParams,
): Promise<FirstTierSupplier[]> => {
  params = params || { skip: 0, limit: 1000 };

  const response = await api.get("first-tier-suppliers/", { params });
  return (
    response.data?.data?.sort((a: FirstTierSupplier, b: FirstTierSupplier) =>
      a.supplier_code.localeCompare(b.supplier_code),
    ) || []
  );
};

export const editFirstTierSupplier = async (
  supplierCode: string,
  payload: EditFirstTierSupplierPayload,
) => {
  const requestPayload = payload.flagged_reason
    ? { ...payload, flagged_reason: Array.from(payload.flagged_reason) }
    : payload;
  const response = await api.put(
    `/first-tier-suppliers/template/${supplierCode}`,
    requestPayload,
  );
  return response.data;
};

export type partSKP = {
  part_id: number;
  part_number: string;
  part_name: string;
  ac: string;
  rt: string;
};

export type part = {
  part_id: number;
  part_number: string;
  part_name: string;
  ac: string;
  rt: string;
  oem_price: {
    current_price: number;
    new_price: number;
  };
  spare_price: {
    current_price: number;
    new_price: number;
  };
  is_active: boolean;
  is_flagged: boolean;
  is_admin_flagged: boolean;
  has_price_in_v2: boolean;
};

export const getPartsBySupplier = async (
  supplierCode: string,
  version: string,
  isActive?: boolean,
): Promise<part[]> => {
  const response = await api.get(
    `/first-tier-suppliers/part-list/${version}/${supplierCode}`,
    {
      params: { is_active: isActive },
    },
  );
  return response.data;
};

export const getPartSKPBySupplier = async (
  supplierCode: string,
): Promise<partSKP[]> => {
  const response = await api.get(
    `/first-tier-suppliers/master-part/${supplierCode}`,
  );
  return response.data;
};

export const insertPartToSupplier = async (
  supplierCode: string,
  part: partSKP,
) => {
  const response = await api.post(
    `/first-tier-suppliers/part/create/${supplierCode}`,
    part,
  );
  return response.data;
};

export const updatePart = async (partIds: Array<number>, isActive: boolean) => {
  const response = await api.put(`/first-tier-suppliers/part/update`, {
    part_ids: partIds,
    payload: { is_active: isActive },
  });
  return response.data;
};

export type duplicatePartPayload = {
  fromPartId: number;
  toPartId: number;
};

export type duplicatePartError = {
  partId: number;
  message: string;
};

export const duplicateParts = async (
  supplierCode: string,
  parts: duplicatePartPayload[],
): Promise<duplicatePartError[]> => {
  const response = await api.post(
    `/first-tier-suppliers/duplicate-parts/${supplierCode}`,
    { parts: parts },
  );
  return response.data;
};
